import type { DisplayJob } from '../types';
import type { JobFormState } from '../redux/features/jobFormSlice';

export interface MatchScore {
  overall: number; // 0-100
  breakdown: {
    title: number; // 0-100
    roleLevel: number; // 0-100
    location: number; // 0-100
    remoteness: number; // 0-100
    jobType: number; // 0-100
    salary: number; // 0-100
    vacation: number; // 0-100
    benefits: number; // 0-100
  };
  details: {
    titleMatch?: string;
    roleLevelMatch?: string;
    locationMatch?: string;
    remotenessMatch?: string;
    jobTypeMatch?: string;
    salaryMatch?: string;
    vacationMatch?: string;
    benefitsMatch?: string[];
  };
}

/**
 * Calculate how well a job matches the user's current role profile
 */
export function calculateJobMatch(
  job: DisplayJob,
  userProfile: JobFormState
): MatchScore | null {
  // If user hasn't filled out their profile, return null
  if (!userProfile.title && !userProfile.normalisedTitle && !userProfile.normalisedRoleLevel) {
    return null;
  }

  const breakdown = {
    title: 0,
    roleLevel: 0,
    location: 0,
    remoteness: 0,
    jobType: 0,
    salary: 0,
    vacation: 0,
    benefits: 0,
  };

  const details: MatchScore['details'] = {};

  // Title match (30% weight)
  if (userProfile.normalisedTitle && job.normalisedTitle) {
    const userTitle = userProfile.normalisedTitle.toLowerCase();
    const jobTitle = job.normalisedTitle.toLowerCase();
    
    if (userTitle === jobTitle) {
      breakdown.title = 100;
      details.titleMatch = 'exact';
    } else if (jobTitle.includes(userTitle) || userTitle.includes(jobTitle)) {
      breakdown.title = 70;
      details.titleMatch = 'partial';
    } else {
      // Check for similar keywords
      const userWords = userTitle.split(/\s+/);
      const jobWords = jobTitle.split(/\s+/);
      const commonWords = userWords.filter(w => jobWords.includes(w));
      if (commonWords.length > 0) {
        breakdown.title = 40;
        details.titleMatch = 'similar';
      } else {
        breakdown.title = 0;
        details.titleMatch = 'none';
      }
    }
  } else if (userProfile.title && job.title) {
    // Fallback to title if normalisedTitle not available
    const userTitle = userProfile.title.toLowerCase();
    const jobTitle = job.title.toLowerCase();
    
    if (userTitle === jobTitle) {
      breakdown.title = 100;
      details.titleMatch = 'exact';
    } else if (jobTitle.includes(userTitle) || userTitle.includes(jobTitle)) {
      breakdown.title = 60;
      details.titleMatch = 'partial';
    } else {
      breakdown.title = 20;
      details.titleMatch = 'none';
    }
  }

  // Role level match (20% weight)
  if (userProfile.normalisedRoleLevel && job.normalisedRoleLevel) {
    const userLevel = userProfile.normalisedRoleLevel.toLowerCase();
    const jobLevel = job.normalisedRoleLevel.toLowerCase();
    
    if (userLevel === jobLevel) {
      breakdown.roleLevel = 100;
      details.roleLevelMatch = 'exact';
    } else {
      // Define level hierarchy for partial matches
      const levelHierarchy: Record<string, number> = {
        'intern': 1,
        'harjoittelija': 1,
        'praktikant': 1,
        'junior': 2,
        'mid-level': 3,
        'keskitaso': 3,
        'medelnivå': 3,
        'senior': 4,
        'senior-taso': 4,
        'expert': 5,
        'asiantuntija': 5,
        'manager': 6,
        'manageri': 6,
        'chef': 6,
        'director': 7,
        'johtaja': 7,
        'direktör': 7,
        'executive': 8,
        'johto': 8,
        'företagsledare': 8,
      };
      
      const userLevelNum = levelHierarchy[userLevel] || 0;
      const jobLevelNum = levelHierarchy[jobLevel] || 0;
      
      if (userLevelNum > 0 && jobLevelNum > 0) {
        const diff = Math.abs(userLevelNum - jobLevelNum);
        if (diff === 0) {
          breakdown.roleLevel = 100;
          details.roleLevelMatch = 'exact';
        } else if (diff === 1) {
          breakdown.roleLevel = 70;
          details.roleLevelMatch = 'one-level-diff';
        } else if (diff === 2) {
          breakdown.roleLevel = 40;
          details.roleLevelMatch = 'two-level-diff';
        } else {
          breakdown.roleLevel = 10;
          details.roleLevelMatch = 'major-diff';
        }
      } else {
        breakdown.roleLevel = 0;
        details.roleLevelMatch = 'none';
      }
    }
  }

  // Location match (15% weight)
  if (userProfile.locations && userProfile.locations.length > 0 && job.location && job.location.length > 0) {
    const userLocations = userProfile.locations.map(l => l.toLowerCase());
    const jobLocations = job.location.map(l => l.toLowerCase());
    
    const matches = jobLocations.filter(jl => 
      userLocations.some(ul => jl.includes(ul) || ul.includes(jl))
    );
    
    if (matches.length > 0) {
      breakdown.location = Math.min(100, (matches.length / jobLocations.length) * 100);
      details.locationMatch = matches.length === jobLocations.length ? 'all' : 'partial';
    } else {
      breakdown.location = 0;
      details.locationMatch = 'none';
    }
  }

  // Remoteness match (10% weight)
  if (userProfile.remoteness && job.remoteness) {
    const userRemoteness = userProfile.remoteness.toLowerCase();
    const jobRemoteness = job.remoteness.toLowerCase();
    
    if (userRemoteness === jobRemoteness) {
      breakdown.remoteness = 100;
      details.remotenessMatch = 'exact';
    } else {
      // Hybrid can match with either Remote or Onsite
      if (userRemoteness === 'hybrid' || jobRemoteness === 'hybrid') {
        breakdown.remoteness = 60;
        details.remotenessMatch = 'partial';
      } else {
        breakdown.remoteness = 0;
        details.remotenessMatch = 'none';
      }
    }
  }

  // Job type match (10% weight)
  if (userProfile.jobType && job.jobType) {
    const userJobType = userProfile.jobType.toLowerCase();
    const jobJobType = job.jobType.toLowerCase();
    
    if (userJobType === jobJobType) {
      breakdown.jobType = 100;
      details.jobTypeMatch = 'exact';
    } else {
      breakdown.jobType = 0;
      details.jobTypeMatch = 'none';
    }
  }

  // Salary match (10% weight)
  // Use desired job minimum salary for matching
  const userDesiredSalaryMin = userProfile.desiredJob?.salaryMin || 0;
  if (userDesiredSalaryMin > 0 && job.salaryRangeMin) {
    const userMin = userDesiredSalaryMin;
    const jobMin = job.salaryRangeMin;
    const jobMax = job.salaryRangeMax || jobMin;
    
    // Check if job salary meets or exceeds user's minimum requirement
    if (jobMin >= userMin) {
      // Job meets or exceeds minimum requirement
      if (jobMax && jobMax > userMin) {
        breakdown.salary = 100;
        details.salaryMatch = 'meets-or-exceeds';
      } else {
        breakdown.salary = 100;
        details.salaryMatch = 'meets-minimum';
      }
    } else if (jobMax >= userMin) {
      // Job max meets minimum (range overlaps)
      breakdown.salary = 90;
      details.salaryMatch = 'overlaps-minimum';
    } else {
      // Job pays less than user's minimum requirement
      const diff = userMin - jobMax;
      const percentDiff = (diff / userMin) * 100;
      if (percentDiff <= 20) {
        breakdown.salary = 60;
        details.salaryMatch = 'slightly-lower';
      } else if (percentDiff <= 50) {
        breakdown.salary = 30;
        details.salaryMatch = 'moderately-lower';
      } else {
        breakdown.salary = 0;
        details.salaryMatch = 'much-lower';
      }
    }
  }

  // Vacation match (5% weight)
  if (userProfile.vacationWeeks > 0 && job.vacationWeeks) {
    if (job.vacationWeeks >= userProfile.vacationWeeks) {
      breakdown.vacation = 100;
      details.vacationMatch = 'meets-or-exceeds';
    } else {
      const diff = userProfile.vacationWeeks - job.vacationWeeks;
      breakdown.vacation = Math.max(0, 100 - (diff * 20));
      details.vacationMatch = 'below';
    }
  }

  // Benefits match (5% weight)
  if (userProfile.benefits && userProfile.benefits.length > 0 && job.benefits && job.benefits.length > 0) {
    const userBenefits = userProfile.benefits.map(b => b.toLowerCase());
    const jobBenefits = job.benefits.map(b => b.toLowerCase());
    
    const matches = jobBenefits.filter(jb => 
      userBenefits.some(ub => jb.includes(ub) || ub.includes(jb))
    );
    
    breakdown.benefits = (matches.length / userBenefits.length) * 100;
    details.benefitsMatch = matches;
  }

  // Calculate weighted overall score
  const weights = {
    title: 0.30,
    roleLevel: 0.20,
    location: 0.15,
    remoteness: 0.10,
    jobType: 0.10,
    salary: 0.10,
    vacation: 0.05,
    benefits: 0.05,
  };

  const overall = Math.round(
    breakdown.title * weights.title +
    breakdown.roleLevel * weights.roleLevel +
    breakdown.location * weights.location +
    breakdown.remoteness * weights.remoteness +
    breakdown.jobType * weights.jobType +
    breakdown.salary * weights.salary +
    breakdown.vacation * weights.vacation +
    breakdown.benefits * weights.benefits
  );

  return {
    overall,
    breakdown,
    details,
  };
}

